import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';

import TextFieldPill from '../../../../01_atoms/FormElements/TextFieldPill';
import Button from '../../../../01_atoms/Button';
import FrayedContainer from '../../../../02_molecules/FrayedContainer';
import FormattedText from '../../../../01_atoms/FormattedText';
import {
  behaviorSettingsProps,
  generateClassNameByBehaviorSettings,
} from '../../../../../utils/behaviorSettings';
import { composeValidators, validEmail, required } from '../../../../../utils/formFieldValidators';
import { parseSpaceless } from '../../../../../utils/formFieldParsers';
import { pushFormSubmission as gtmPushFormSubmission } from '../../../../../utils/gtm';
import fetch from '../../../../../utils/fetch';
import unexpectedErrorMessage from '../../../../../utils/unexpectedErrorMessage';
import { toast } from 'react-toastify';

import styles from './index.module.scss';

const BBNewsletterDotdigitalSignUp = ({
  title = '',
  addressBookId = 0,
  optInType = '',
  subtitle = '',
  description = '',
  firstnamePlaceholder = '',
  lastnamePlaceholder = '',
  emailPlaceholder = '',
  submitLabel = 'Submit',
  showNameFields = false,
  behaviorSettings = null,
  uuid = null,
}) => {
  const id = useId();

  const classes = [
    'bb',
    'bb-frayed-edge-text-form',
    styles['bb-frayed-edge-text-form'],
    showNameFields
      ? styles['bb-frayed-edge-text-form--with-names']
      : styles['bb-frayed-edge-text-form--without-names'],
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  const onSubmit = ({ email, firstname, lastname }, form) => {
    const payload = {
      email,
      firstname,
      lastname,
      address_book_id: addressBookId,
      opt_in_type: optInType,
    };

    // Generating form ID for GTM from block title and address book ID.
    const formId = `${title.replace(/\W/g, '_').toLowerCase()}_${addressBookId}`;

    return new Promise((resolve, reject) => {
      // Send an event to GTM.
      gtmPushFormSubmission(formId);

      fetch('/dotdigital-subscribe', {
        method: 'POST',
        throwNotOk: false,
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((resp) => {
          if (resp.statusCode === 200) {
            toast.success('Thanks for subscribing!', { autoClose: 7000 });
            resolve(resp);
            return resp;
          }

          if (resp.statusCode === 400 && resp.body === 'Invalid email address') {
            toast.error(
              `The ${email} email address is not allowed. Please try to use another email address.`,
              { autoClose: 7000 },
            );
          } else {
            toast.error(
              <div
                dangerouslySetInnerHTML={{
                  __html: `Sorry, something went wrong. ${unexpectedErrorMessage()}`,
                }}
              />,
            );
          }

          resolve();
          return resp;
        })
        .then((resp) => {
          if (resp.statusCode === 200) {
            form.restart();

            if (form.getFieldState('firstname')) {
              form.resetFieldState('firstname');
            }

            if (form.getFieldState('lastname')) {
              form.resetFieldState('lastname');
            }

            form.resetFieldState('email');
          }
        })
        .catch((error) => {
          toast.error(
            <div
              dangerouslySetInnerHTML={{
                __html: `Sorry, something went wrong. ${unexpectedErrorMessage()}`,
              }}
            />,
          );
          reject(error);
          return error;
        });
    });
  };

  return (
    <div className={classes.join(' ')} id={uuid}>
      <FrayedContainer>
        {title && <h2>{title}</h2>}
        <div className="container">
          {subtitle && <p className="subtitle">{subtitle}</p>}

          <Form
            onSubmit={onSubmit}
            subscription={{ submitting: true, valid: true }}
            render={({ handleSubmit, submitting, valid }) => (
              <form onSubmit={handleSubmit}>
                {showNameFields && (
                  <div className="row">
                    <div className="col-12 col-md-6 ms-md-auto text-field">
                      <TextFieldPill
                        name="firstname"
                        placeholder={firstnamePlaceholder}
                        color="green"
                        validate={required()}
                        isRequired={true}
                        id={`firstname-${id}`}
                      />
                    </div>
                    <div className="col-12 col-md-6 ms-md-auto text-field">
                      <TextFieldPill
                        name="lastname"
                        placeholder={lastnamePlaceholder}
                        color="green"
                        validate={required()}
                        isRequired={true}
                        id={`lastname-${id}`}
                      />
                    </div>
                  </div>
                )}
                <div className="row">
                  <div
                    className={`col-12 text-field ${!showNameFields ? 'col-md-auto ms-md-auto' : ''}`}
                  >
                    <TextFieldPill
                      name="email"
                      placeholder={emailPlaceholder}
                      color="green"
                      parse={parseSpaceless}
                      validate={composeValidators(required(), validEmail())}
                      isRequired={true}
                      id={`email-${id}`}
                    />
                  </div>

                  <div
                    className={`col-12 text-field ${!showNameFields ? 'col-md-auto me-md-auto' : ''}`}
                  >
                    <Button
                      type="white-green"
                      isMobileBlock
                      isOutlined
                      isLoading={submitting}
                      className="bb-frayed-edge-text-form--button"
                    >
                      {submitLabel}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          />

          {description && <FormattedText tag="mark" className="description" text={description} />}
        </div>
      </FrayedContainer>
    </div>
  );
};

BBNewsletterDotdigitalSignUp.propTypes = {
  title: PropTypes.string,
  addressBookId: PropTypes.number,
  optInType: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  firstnamePlaceholder: PropTypes.string,
  lastnamePlaceholder: PropTypes.string,
  emailPlaceholder: PropTypes.string,
  submitLabel: PropTypes.string,
  showNameFields: PropTypes.bool,
  behaviorSettings: behaviorSettingsProps,
  uuid: PropTypes.string,
};

export default BBNewsletterDotdigitalSignUp;
